import JsonLd from '@allesauto/core/JsonLd';
import {
  StoryblokSiteConfig,
  useWhiteLabel,
  WhiteLabelConfig,
  WhiteLabelProvider,
} from '@allesauto/core/whiteLabel';
import translate from '@allesauto/i18n';
import { SupportedLocales } from '@allesauto/utils/consts';
import { getIntegrationSlugFromHost } from '@allesauto/utils/funcs';
import { customRichTextSchema } from '@allesauto/utils/storyblok';
import { ApolloProvider } from '@apollo/client';
import { apiPlugin, storyblokInit } from '@storyblok/react';
import App, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useEffect, useMemo, useRef } from 'react';
import { CookiesProvider } from 'react-cookie';
import { IntlProvider, useIntl } from 'react-intl';
import { Organization } from 'schema-dts';
import smoothscroll from 'smoothscroll-polyfill';

import '@/components/Footer/Footer.css';
import '@/components/forms/NewsletterForms/NewsletterForms.css';
import '@/components/forms/ServiceBookingContactForm/ServiceBookingContactForm.css';
import '@/components/PDP/PDPTemplate.css';
import '@/components/ReCaptcha/ReCaptchaValidator.css';
import '@allesauto/core/style.css';
import '../styles/globals.css';
import '../styles/HomePage.css';
import '../styles/HeaderContainerOverrides.css';

import AppVersion from '@/components/AppVersion/AppVersion';
import MaintenanceComponent from '@/components/Maintenance/Maintenance';

import {
  getSchneiderGruppeNavigation,
  getWhiteLabelConfig,
} from '@/features/whitelabel/util/getWhiteLabelConfig';
import { WL_INTEGRATION_SLUG_AUTO_LEVY } from '@/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import { useApollo } from '@/global/apollo/apollo';
import { IDeviceType, initialDeviceType } from '@/global/context/deviceType';
import DynamicOverlay from '@/global/dynamicComponents/DynamicOverlay';
import DynamicToastify from '@/global/dynamicComponents/DynamicToastify';
import { deviceDetect } from '@/global/funcs/detectDevice';
import {
  RouterHistoryContext,
  useRouterHistory,
} from '@/global/hooks/routerHistory';
import { useCookieManager } from '@/global/hooks/useCookieManager';
import { useGTM } from '@/global/hooks/useGTM';
import { useMTM } from '@/global/hooks/useMTM';
import { AuthProvider } from '@/global/providers/auth';
import { DeviceTypeProvider } from '@/global/providers/deviceType';
import StatePersistProvider from '@/global/providers/statePersist';
import { ReduxProvider } from '@/store/redux';

import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';

type MyAppProps = AppProps & {
  whiteLabel?: WhiteLabelConfig;
  deviceType: IDeviceType;
  intlMessages: Record<string, Record<string, string>>;
};

if (typeof window !== 'undefined') {
  smoothscroll.polyfill();
} else {
  React.useLayoutEffect = React.useEffect;
}

const OrganizationSchema = () => {
  const intl = useIntl();
  const whitelabel = useWhiteLabel();

  const organization: Organization = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      address: whitelabel
        ? undefined
        : {
            '@type': 'PostalAddress',
            addressLocality: translate(intl, 'meta.ORGANIZATION.CITY'),
            postalCode: translate(intl, 'meta.ORGANIZATION.ZIP'),
            streetAddress: translate(intl, 'meta.ORGANIZATION.STREET_NO'),
          },
      brand: whitelabel
        ? whitelabel.make
        : translate(intl, 'meta.ORGANIZATION.BRAND'),
      name: whitelabel
        ? whitelabel.merchantName
        : translate(intl, 'meta.ORGANIZATION.NAME'),
      telephone: whitelabel
        ? undefined
        : translate(intl, 'meta.ORGANIZATION.PHONE'),
      faxNumber: whitelabel
        ? undefined
        : translate(intl, 'meta.ORGANIZATION.FAX'),
      email: whitelabel
        ? undefined
        : translate(intl, 'meta.ORGANIZATION.EMAIL'),
      logo: whitelabel?.logo,
    }),
    [whitelabel, intl]
  );

  return <JsonLd id="schema-org-organization" value={organization} />;
};

// Storyblok integration, see https://www.storyblok.com/mp/announcing-storyblok-react
storyblokInit({
  accessToken: process.env.STORYBLOK_API_TOKEN,
  richText: {
    schema: customRichTextSchema,
  },
  use: [apiPlugin],
});

function MyApp({
  Component,
  pageProps,
  whiteLabel: initialWhiteLabel,
  deviceType = initialDeviceType,
  intlMessages,
}: MyAppProps) {
  const whiteLabel = useRef(initialWhiteLabel).current; // initialize once with server-side props and preserve original on client-side navigation
  const dealerIds = whiteLabel?.merchantUuids;
  const merchantOrigin = whiteLabel?.merchantOrigin;
  const apolloClient = useApollo(pageProps, dealerIds, merchantOrigin);
  const { asPath, route } = useRouter();
  const previousRoute = useRouterHistory();
  const { addWhiteLabelThemeClass, addImprintAndPrivacyLinks } =
    useCookieManager();

  const { setVirtualPageview } = useGTM();
  const useMatomo = useMTM(whiteLabel);

  // Push global page information to the GTM’s dataLayer
  const pushVirtualPageview = () =>
    setVirtualPageview(route, asPath, document.title);
  useEffect((): void => {
    if (!whiteLabel) return;

    // Add whitelabel theme to cookie manager root element
    const ccmWidget = document.querySelector('.ccm-root');
    addWhiteLabelThemeClass(ccmWidget);
    // Add merchant's Privacy Policy and Imprint links programmatically
    const linkContainer = document.querySelector(
      '.ccm-widget .ccm-link-container'
    );
    addImprintAndPrivacyLinks(
      linkContainer,
      whiteLabel.imprintUrl,
      whiteLabel.dataPrivacyUrl
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!window.CCM) return;

    // Listener for cookie manager to be closed
    window.addEventListener('ccm19WidgetClosed', () => {
      pushVirtualPageview();

      // a workaround for a CCM bug. we don't use sessionStorage for CCM
      // but it saves to it when we just close the widget and it breaks further usage
      // so we need to remove the value from sessionStorage every time we close it
      if (window.sessionStorage) {
        window.sessionStorage.removeItem('ccm_consent');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): void => {
    pushVirtualPageview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  const isTradeInPreview = useMemo(() => {
    return route === '/[dealer]/preview/pages/[...slug]';
  }, [route]);

  return (
    <>
      <Head>
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        {process.env.NODE_ENV !== 'development' && (
          <script
            id="matomo-tag-manager"
            dangerouslySetInnerHTML={{
              __html: useMatomo,
            }}
          />
        )}
      </Head>
      {whiteLabel?.subdomain === WL_INTEGRATION_SLUG_AUTO_LEVY && (
        <Script
          id="levy-gtm"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-NM8CJ4BQ');`,
          }}
        ></Script>
      )}
      <IntlProvider
        //locale={locale ?? SupportedLocales.DEFAULT}
        // messages={messages[locale ?? SupportedLocales.DEFAULT]}
        //TODO remove hardcoded locale once we support more languages
        locale={SupportedLocales.DEFAULT}
        messages={intlMessages[SupportedLocales.DEFAULT]}
      >
        <ErrorBoundary>
          {whiteLabel || isTradeInPreview ? (
            <ApolloProvider client={apolloClient}>
              <ReduxProvider>
                <StatePersistProvider>
                  <AuthProvider>
                    <DeviceTypeProvider value={deviceType}>
                      <WhiteLabelProvider value={whiteLabel}>
                        <RouterHistoryContext.Provider value={previousRoute}>
                          <AppVersion />
                          <Component {...pageProps} />
                        </RouterHistoryContext.Provider>
                        <DynamicToastify />
                        <CookiesProvider />
                        <DynamicOverlay />
                        <OrganizationSchema />
                      </WhiteLabelProvider>
                    </DeviceTypeProvider>
                  </AuthProvider>
                </StatePersistProvider>
              </ReduxProvider>
            </ApolloProvider>
          ) : (
            <MaintenanceComponent />
          )}
        </ErrorBoundary>
      </IntlProvider>
    </>
  );
}

function loadIntlMessages() {
  return import('@allesauto/i18n/dist/de-DE.json');
}

MyApp.getInitialProps = async (
  context: AppContext
): Promise<AppProps['pageProps']> => {
  const props: AppProps['pageProps'] = await App.getInitialProps(context);

  if (typeof window === 'undefined') {
    // execute only on the server-side, not on client-side navigation
    const integrationSlug = getIntegrationSlugFromHost(
      context.ctx.req?.headers?.host || ''
    );
    let config = await getWhiteLabelConfig(integrationSlug);
    const deviceType = deviceDetect(
      context.ctx?.req?.headers['user-agent'] || ''
    );

    if (config) {
      if (config.storyBlokResponse) {
        try {
          const storyBlokResponse: StoryblokSiteConfig = JSON.parse(
            config.storyBlokResponse
          );
          storyBlokResponse.logo = config.logo;
          if (
            storyBlokResponse.header_layout === 'SchneiderGruppe' &&
            storyBlokResponse.navigation_api_link != undefined
          ) {
            const schneiderCustomNavigation =
              await getSchneiderGruppeNavigation(
                storyBlokResponse.navigation_api_link.url
              );

            if (schneiderCustomNavigation) {
              storyBlokResponse.schneiderCustomNavigation =
                schneiderCustomNavigation;
            }
          }

          // Extend the config object with the Storyblok config
          config = { ...config, storyBlokResponse };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Could not parse SiteConfig from storyBlokResponse');
        }
      }

      (
        props as AppProps['pageProps'] & { whiteLabel: WhiteLabelConfig }
      ).whiteLabel = config;
      // @ts-expect-error pass whitelabel data to _document.tsx through context
      context.ctx.whitelabel = config;
    }
    if (deviceType) {
      (
        props as AppProps['pageProps'] & { deviceType: IDeviceType }
      ).deviceType = deviceType;
    }
  }

  const intlMessages = await loadIntlMessages();

  props.intlMessages = {
    [SupportedLocales.DE]: intlMessages.default,
  };

  return props;
};

export default MyApp;
